import type { CSSProperties, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Icon, type IconName } from '../Icon.js';

interface KpiPanelProps {
  icon: IconName;
  num: string;
  numColor: string;
  label: string;
  linkText: string;
  linkTo: string;
  heading: string;
  rowsLinkText?: string | undefined;
  rowsLinkTo?: string | undefined;
  style?: CSSProperties;
  children: ReactNode;
}

export function KpiPanel({
  icon,
  num,
  numColor,
  label,
  linkText,
  linkTo,
  heading,
  rowsLinkText,
  rowsLinkTo,
  style,
  children,
}: KpiPanelProps) {
  return (
    <section
      style={{
        background: 'rgba(38,47,54,0.20)',
        display: 'grid',
        gridTemplateColumns: '144px 430px 1fr',
        gridTemplateRows: '1fr auto',
        ...style,
      }}
    >
      {/* Col 1 — icon */}
      <div
        style={{
          gridColumn: 1,
          gridRow: '1 / span 2',
          padding: '24px 32px 0',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          color: numColor,
        }}
      >
        <Icon name={icon} size={80} strokeWidth={1} />
      </div>

      {/* Col 2 — number, label, link */}
      <div
        style={{
          gridColumn: 2,
          gridRow: '1 / span 2',
          padding: '24px 32px',
          display: 'grid',
          gridTemplateColumns: '1fr auto',
          gridTemplateRows: '1fr auto',
          rowGap: 24,
          borderLeft: '1px solid var(--evo-stroke)',
          borderRight: '1px solid var(--evo-stroke)',
        }}
      >
        <span
          style={{
            gridColumn: '1 / span 2',
            gridRow: 1,
            justifySelf: 'end',
            alignSelf: 'start',
            fontSize: 200,
            lineHeight: 0.85,
            color: numColor,
            fontWeight: 400,
            letterSpacing: '-0.04em',
            fontVariantNumeric: 'tabular-nums',
            fontFamily: 'var(--evo-font)',
          }}
        >
          {num}
        </span>
        <span
          style={{
            gridColumn: 1,
            gridRow: 2,
            alignSelf: 'end',
            justifySelf: 'start',
            fontSize: 18,
            lineHeight: 1.05,
            letterSpacing: '0.88px',
            textTransform: 'uppercase',
            color: 'var(--evo-fg-2)',
            fontWeight: 500,
            fontFamily: 'var(--evo-font)',
            whiteSpace: 'pre-line',
          }}
        >
          {label}
        </span>
        <Link
          to={linkTo}
          style={{
            gridColumn: 2,
            gridRow: 2,
            alignSelf: 'end',
            justifySelf: 'end',
            color: 'var(--evo-cyan)',
            fontSize: 13,
            textDecoration: 'none',
            whiteSpace: 'nowrap',
          }}
        >
          {linkText}
        </Link>
      </div>

      {/* Col 3 — list */}
      <div
        style={{
          gridColumn: 3,
          gridRow: '1 / span 2',
          padding: '24px var(--evo-content-pad-x)',
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
        }}
      >
        <span
          style={{
            fontSize: 18,
            letterSpacing: '0.88px',
            textTransform: 'uppercase',
            color: 'var(--evo-fg-2)',
            fontWeight: 500,
            marginBottom: 8,
            fontFamily: 'var(--evo-font)',
          }}
        >
          {heading}
        </span>
        <div style={{ flex: 1 }}>{children}</div>
        {rowsLinkText && rowsLinkTo && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
            <Link
              to={rowsLinkTo}
              style={{ color: 'var(--evo-cyan)', fontSize: 13, textDecoration: 'none' }}
            >
              {rowsLinkText}
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}
